/**
 * Local search over an authorised trip document.
 * Distinguishes bookings, places, notes and references.
 */

import type { TripDocument } from "./schema.ts";

export type SearchHit = {
  id: string;
  kind: "booking" | "place" | "event" | "flight" | "note" | "contact" | "reference";
  title: string;
  subtitle: string;
  href: string;
};

function hay(value: unknown): string {
  return typeof value === "string" ? value.toLowerCase() : "";
}

export function searchTrip(doc: TripDocument, query: string): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const hits: SearchHit[] = [];

  for (const booking of doc.bookings) {
    const blob = [
      booking.title,
      booking.provider,
      booking.confirmation,
      booking.expediaItinerary,
      booking.roomName,
      ...(booking.notes ?? []),
    ]
      .map(hay)
      .join(" ");
    if (blob.includes(q)) {
      const kind = booking.confirmation && hay(booking.confirmation).includes(q) ? "reference" : "booking";
      hits.push({
        id: booking.id,
        kind,
        title: booking.title ?? booking.provider ?? booking.roomName ?? booking.id,
        subtitle: [booking.confirmation, booking.kind.replaceAll("_", " ")].filter(Boolean).join(" · "),
        href: `/bookings/${booking.id}`,
      });
    }
  }

  for (const place of doc.places) {
    const blob = [place.name, place.address, place.thaiName].map(hay).join(" ");
    if (blob.includes(q)) {
      hits.push({
        id: place.id,
        kind: "place",
        title: place.name,
        subtitle: place.address ?? "Address to add",
        href: `/map?place=${place.id}`,
      });
    }
  }

  for (const flight of doc.flights) {
    const blob = [flight.number, flight.airline, flight.departure.airport, flight.arrival.airport, flight.pnr]
      .map(hay)
      .join(" ");
    if (blob.includes(q)) {
      hits.push({
        id: flight.id,
        kind: "flight",
        title: `${flight.airline} ${flight.number}`,
        subtitle: `${flight.departure.airport} → ${flight.arrival.airport}`,
        href: `/journey/flight/${flight.id}`,
      });
    }
  }

  for (const event of doc.events) {
    const blob = [event.title, event.pickupInstruction, event.meetingNote, ...(event.notes ?? [])]
      .map(hay)
      .join(" ");
    if (blob.includes(q)) {
      hits.push({
        id: event.id,
        kind: "event",
        title: event.title ?? event.kind,
        subtitle: event.date,
        href: event.kind === "transfer" ? `/journey/transfer/${event.id}` : `/journey/event/${event.id}`,
      });
    }
  }

  for (const contact of doc.contacts) {
    const blob = [contact.name, contact.role, contact.notes].map(hay).join(" ");
    if (blob.includes(q)) {
      hits.push({
        id: contact.id,
        kind: "contact",
        title: contact.name,
        subtitle: contact.role,
        href: `/essentials#contact-${contact.id}`,
      });
    }
  }

  for (const item of doc.reviewItems) {
    if (hay(item.message).includes(q)) {
      hits.push({
        id: item.id,
        kind: "note",
        title: "Review item",
        subtitle: item.message,
        href: `/essentials/review`,
      });
    }
  }

  return hits.slice(0, 30);
}
